import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import axios from 'axios';
import Sidebar from './components/Sidebar';
import { API_URL, SOCKET_URL } from './config';
import './Dashboard.css';

const socket = io(SOCKET_URL);

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function Dashboard() {
  const navigate = useNavigate();
  const [status, setStatus] = useState({ status: 'initializing', message: 'Initializing...' });
  const [user, setUser] = useState(null);
  const [contactsCount, setContactsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    socket.on('status', (statusData) => {
      setStatus(statusData);
    });

    checkStatus();
    fetchContactsCount();

    return () => {
      socket.off('status');
    };
  }, []);

  const checkStatus = async () => {
    try {
      const res = await api.get('/api/status');
      if (res.data.ready) {
        setStatus({ status: 'ready', message: 'WhatsApp is connected and ready!' });
      } else {
        setStatus({ status: 'not_ready', message: 'WhatsApp is not connected. Please scan QR code.' });
      }
    } catch (error) {
      console.error('Error checking status:', error);
      setStatus({ status: 'error', message: 'Unable to connect to server' });
    } finally {
      setLoading(false);
    }
  };

  const fetchContactsCount = async () => {
    try {
      const res = await api.get('/api/contacts');
      if (res.data.success) {
        setContactsCount(res.data.contacts?.length || 0);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const getStatusColor = () => {
    switch (status.status) {
      case 'ready':
        return '#10b981';
      case 'scan':
        return '#f59e0b';
      case 'error':
      case 'auth_failure':
      case 'not_ready':
        return '#ef4444';
      default:
        return '#3b82f6';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  return (
    <div className="clean-dashboard">
      {/* Sidebar */}
      <Sidebar 
        onLogout={handleLogout} 
        isOpen={sidebarOpen}
        onToggle={setSidebarOpen}
      />

      {/* Main Content */}
      <div className={`main-wrapper-clean ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Top Bar */}
        <header className="top-bar-clean">
          <div className="top-left">
            <button className="menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              ☰
            </button>
            <h1 className="page-title-clean">Dashboard</h1>
          </div>
          <div className="top-right">
            <div className="user-menu-clean">
              <div className="user-avatar-small">{user?.user_id?.[0]?.toUpperCase() || 'U'}</div>
              <span className="user-name-small">{user?.user_id || 'User'}</span>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="dashboard-content-clean">
          {/* Welcome Section */}
          <div className="welcome-banner">
            <div className="welcome-text">
              <h2>Welcome back, {user?.user_id || 'User'}! 👋</h2>
              <p>Manage your WhatsApp messages and contacts from here.</p>
            </div>
            <div className="welcome-actions">
              <button className="btn-primary-modern" onClick={() => navigate('/messages')}>
                📤 Send Message
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid-clean">
            <div className="stat-box stat-box-1">
              <div className="stat-header-box">
                <span className="stat-label-box">Total Contacts</span>
                <span className="stat-icon-box">📇</span>
              </div>
              <div className="stat-value-box">{contactsCount}</div>
              <div className="stat-footer-box">
                <button onClick={() => navigate('/contacts')} className="stat-link-box">
                  View all →
                </button>
              </div>
            </div>

            <div className="stat-box stat-box-2">
              <div className="stat-header-box">
                <span className="stat-label-box">WhatsApp Status</span>
                <span className="stat-icon-box">💬</span>
              </div>
              <div className="stat-value-box">{status.status === 'ready' ? 'Connected' : 'Disconnected'}</div>
              <div className="stat-footer-box">
                <button onClick={() => navigate('/qr')} className="stat-link-box">
                  {status.status === 'ready' ? 'View' : 'Connect'} →
                </button>
              </div>
            </div>

            <div className="stat-box stat-box-3">
              <div className="stat-header-box">
                <span className="stat-label-box">Messages Sent</span>
                <span className="stat-icon-box">📤</span>
              </div>
              <div className="stat-value-box">0</div>
              <div className="stat-footer-box">
                <button onClick={() => navigate('/messages')} className="stat-link-box">
                  Send now →
                </button>
              </div>
            </div>

            <div className="stat-box stat-box-4">
              <div className="stat-header-box">
                <span className="stat-label-box">API Access</span>
                <span className="stat-icon-box">🔗</span>
              </div>
              <div className="stat-value-box">Ready</div>
              <div className="stat-footer-box">
                <button onClick={() => navigate('/api-info')} className="stat-link-box">
                  View API →
                </button>
              </div>
            </div>
          </div>

          {/* Main Cards */}
          <div className="cards-grid-clean">
            {/* Status Card */}
            <div className="card-modern status-card-modern">
              <div className="card-header-modern">
                <h3>Connection Status</h3>
              </div>
              <div className="card-body-modern">
                <div className="status-wrapper">
                  <div className="status-dot-modern" style={{ background: getStatusColor() }}></div>
                  <div className="status-info-modern">
                    <h4>{status.message}</h4>
                    <p>Last updated: {new Date().toLocaleTimeString()}</p>
                  </div>
                </div>
                {status.status !== 'ready' && (
                  <button 
                    className="btn-modern btn-primary-modern"
                    onClick={() => navigate('/qr')}
                  >
                    📷 Scan QR Code
                  </button>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="card-modern">
              <div className="card-header-modern">
                <h3>Quick Actions</h3>
              </div>
              <div className="card-body-modern">
                <div className="quick-actions-list">
                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate('/messages')}
                  >
                    <div className="action-icon-modern">📤</div>
                    <div className="action-text-modern">
                      <div className="action-title-modern">Send Message</div>
                      <div className="action-desc-modern">Send WhatsApp message</div>
                    </div>
                  </button>

                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate('/contacts')}
                  >
                    <div className="action-icon-modern">📇</div>
                    <div className="action-text-modern">
                      <div className="action-title-modern">Manage Contacts</div>
                      <div className="action-desc-modern">Add or edit contacts</div>
                    </div>
                  </button>

                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate('/qr')}
                  >
                    <div className="action-icon-modern">📷</div>
                    <div className="action-text-modern">
                      <div className="action-title-modern">Scan QR Code</div>
                      <div className="action-desc-modern">Connect WhatsApp</div>
                    </div>
                  </button>

                  <button 
                    className="quick-action-btn"
                    onClick={() => navigate('/api-info')}
                  >
                    <div className="action-icon-modern">🔗</div>
                    <div className="action-text-modern">
                      <div className="action-title-modern">API Endpoints</div>
                      <div className="action-desc-modern">View documentation</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
